import React, { useState } from "react";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { SURFACE } from "../../../dark-theme/palette";
import SocialShareDropdown from "../../puzzles/common/components/SocialShareDropdown";
import { usePagesTranslation } from "../../locales";

const StaticToolbar = ({ puzzleTitle = "Sample Puzzle", puzzleId = "sample-id", puzzleType = "HIDDEN_MESSAGE" }) => {
    const { t } = usePagesTranslation();
    const [selectedButton, setSelectedButton] = useState(null);

    const handleCopyLink = () => {
        // Static handler - no action needed
    };

    const handleButtonClick = (buttonId) => {
        // Toggle: if clicking the same button, deselect it; otherwise select the new one
        setSelectedButton(selectedButton === buttonId ? null : buttonId);
    };

    return (
        <Box sx={{
            p: 2,
            pb: 1,
            flexShrink: 0,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            overflowY: 'hidden',
            backgroundColor: SURFACE.chrome,
            borderRadius: '8px',
            border: `2px solid ${SURFACE.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            boxSizing: 'border-box'
        }}>
            <Box sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                flexWrap: 'wrap'
            }}>
                {/* Back Button */}
                <Tooltip title="Go back" arrow>
                    <IconButton
                        onClick={() => handleButtonClick('back')}
                        sx={{
                            backgroundColor: selectedButton === 'back' ? '#3BA666' : 'rgba(200, 200, 200, 0.5)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: selectedButton === 'back' ? '#fff' : 'inherit',
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: selectedButton === 'back' ? '#2E8B57' : 'rgba(200, 200, 200, 0.7)',
                            }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip
                    title={
                        selectedButton === "bold"
                            ? t("puzzle.playGrid.switch-light")
                            : t("puzzle.playGrid.switch-bold")
                    }
                    arrow
                >
                    <IconButton
                        onClick={() => handleButtonClick("bold")}
                        sx={{
                            backgroundColor: selectedButton === "bold" ? "#3BA666" : "rgba(200, 200, 200, 0.5)",
                            borderRadius: "50%",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            color: selectedButton === "bold" ? "#fff" : "inherit",
                            flexShrink: 0,
                            "&:hover": {
                                backgroundColor:
                                    selectedButton === "bold" ? "#2E8B57" : "rgba(200, 200, 200, 0.7)",
                            },
                        }}
                    >
                        <FormatBoldIcon />
                    </IconButton>
                </Tooltip>

                {/* PDF — copy matches howto.crossword.btn-pdf */}
                <Tooltip title={t("howto.crossword.btn-pdf")} arrow>
                    <IconButton
                        onClick={() => handleButtonClick('pdf')}
                        sx={{
                            backgroundColor: selectedButton === 'pdf' ? '#3BA666' : 'rgba(200, 200, 200, 0.5)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: selectedButton === 'pdf' ? '#fff' : 'inherit',
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: selectedButton === 'pdf' ? '#2E8B57' : 'rgba(200, 200, 200, 0.7)',
                            }
                        }}
                    >
                        <PictureAsPdfIcon />
                    </IconButton>
                </Tooltip>

                {/* Social Share Dropdown */}
                <Box sx={{ flexShrink: 0 }}>
                    <SocialShareDropdown
                        puzzleTitle={puzzleTitle}
                        puzzleId={puzzleId}
                        puzzleType={puzzleType}
                        onCopyLink={handleCopyLink}
                        isDemo={true}
                    />
                </Box>

                {/* Clear Selection Button */}
                <Tooltip title="Clear selected words" arrow>
                    <IconButton
                        onClick={() => handleButtonClick('clear')}
                        sx={{
                            backgroundColor: selectedButton === 'clear' ? '#3BA666' : 'rgba(200, 200, 200, 0.5)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: selectedButton === 'clear' ? '#fff' : 'inherit',
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: selectedButton === 'clear' ? '#2E8B57' : 'rgba(200, 200, 200, 0.7)',
                            }
                        }}
                    >
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>

                {/* Fullscreen Toggle Button */}
                <Tooltip title="Show puzzle fullscreen" arrow>
                    <IconButton
                        onClick={() => handleButtonClick('fullscreen')}
                        sx={{
                            backgroundColor: selectedButton === 'fullscreen' ? '#3BA666' : 'rgba(200, 200, 200, 0.5)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: selectedButton === 'fullscreen' ? '#fff' : 'inherit',
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: selectedButton === 'fullscreen' ? '#2E8B57' : 'rgba(200, 200, 200, 0.7)',
                            }
                        }}
                    >
                        <FullscreenIcon />
                    </IconButton>
                </Tooltip>

                {/* Words List Toggle Button */}
                <Tooltip title="Toggle Words List" arrow>
                    <IconButton
                        onClick={() => handleButtonClick('wordsList')}
                        sx={{
                            backgroundColor: selectedButton === 'wordsList' ? '#3BA666' : 'rgba(200, 200, 200, 0.5)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: selectedButton === 'wordsList' ? '#fff' : 'inherit',
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: selectedButton === 'wordsList' ? '#2E8B57' : 'rgba(200, 200, 200, 0.7)',
                            }
                        }}
                    >
                        <LibraryBooksIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            {/* Note about toolbar buttons */}
            <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        fontSize: '0.75rem'
                    }}
                >
                    Note: Buttons on the toolbar don't work here. This is a manual page.
                </Typography>
            </Box>
        </Box>
    );
};

export default StaticToolbar;

